/**
 * Settings → Profile. Per-user personal info — name, email
 * (change-with-code flow), org switcher, timezone, phone, telegram.
 */

import { useEffect, useMemo, useState } from "react";
import { Btn } from "@/components/Btn";
import { I } from "@/components/Icon";
import { StatusToast } from "@/components/StatusToast";
import { NotificationChannelsCard } from "@/components/notifications/NotificationChannelsCard";
import {
  useEmailChangeConfirm,
  useEmailChangeInit,
  useUpdateProfile,
} from "@/lib/api-hooks";
import { useAuth } from "@/lib/useAuth";
import { StSectionHead } from "./_shared";

// Dropdown options: value = bare IANA name (`Europe/Moscow`) so it's
// directly usable by Python's zoneinfo / our notifications scheduler.
// `label` keeps the GMT-offset hint for humans. The pre-fix UI stored
// the full label as value — backend's `_resolve_tz` strips the suffix
// for backward compatibility, so legacy rows keep working until users
// re-save their profile.
const TZ_OPTIONS: { value: string; label: string }[] = [
  { value: "Pacific/Honolulu", label: "Pacific/Honolulu (GMT-10)" },
  { value: "America/Los_Angeles", label: "America/Los_Angeles (GMT-8)" },
  { value: "America/Denver", label: "America/Denver (GMT-7)" },
  { value: "America/Chicago", label: "America/Chicago (GMT-6)" },
  { value: "America/New_York", label: "America/New_York (GMT-5)" },
  { value: "America/Sao_Paulo", label: "America/Sao_Paulo (GMT-3)" },
  { value: "Atlantic/Azores", label: "Atlantic/Azores (GMT-1)" },
  { value: "Europe/London", label: "Europe/London (GMT+0)" },
  { value: "Europe/Berlin", label: "Europe/Berlin (GMT+1)" },
  { value: "Europe/Moscow", label: "Europe/Moscow (GMT+3)" },
  { value: "Asia/Tbilisi", label: "Asia/Tbilisi (GMT+4)" },
  { value: "Asia/Dubai", label: "Asia/Dubai (GMT+4)" },
  { value: "Asia/Karachi", label: "Asia/Karachi (GMT+5)" },
  { value: "Asia/Kolkata", label: "Asia/Kolkata (GMT+5:30)" },
  { value: "Asia/Bangkok", label: "Asia/Bangkok (GMT+7)" },
  { value: "Asia/Shanghai", label: "Asia/Shanghai (GMT+8)" },
  { value: "Asia/Tokyo", label: "Asia/Tokyo (GMT+9)" },
  { value: "Australia/Sydney", label: "Australia/Sydney (GMT+11)" },
];

const COUNTRY_CODES = [
  "+1",
  "+7",
  "+33",
  "+34",
  "+44",
  "+49",
  "+86",
  "+91",
  "+995",
  "+971",
];

export function ProfileTab() {
  const { user, logout, switchOrg } = useAuth();
  const update = useUpdateProfile();
  const initEmail = useEmailChangeInit();
  // Cleared as soon as the code request goes out — no reason to keep it
  // in component state through the confirmation step.
  const [emailPassword, setEmailPassword] = useState("");
  const confirmEmail = useEmailChangeConfirm();

  const initial = useMemo(
    () => ({
      first: (user?.full_name ?? "").split(" ")[0] ?? "",
      last: (user?.full_name ?? "").split(" ").slice(1).join(" "),
      email: user?.email ?? "",
      timezone: user?.timezone ?? "",
      phoneCc: user?.phone_cc ?? "+1",
      phone: user?.phone_number ?? "",
      telegram: user?.telegram ?? "",
    }),
    [user],
  );

  const [form, setForm] = useState(initial);
  useEffect(() => setForm(initial), [initial]);

  const [emailFlow, setEmailFlow] = useState<0 | 1 | 2 | null>(null);
  const [newEmail, setNewEmail] = useState("");
  const [code, setCode] = useState("");

  const dirty = JSON.stringify(form) !== JSON.stringify(initial);
  const set = <K extends keyof typeof form>(k: K, v: (typeof form)[K]) =>
    setForm((f) => ({ ...f, [k]: v }));

  // Floating green/red toast confirming save outcome. Without this
  // the user sees nothing happen on success — there's no other
  // visible signal beyond `dirty` flipping back to false.
  const [saveToast, setSaveToast] = useState<
    { msg: string; variant: "success" | "error" } | null
  >(null);
  useEffect(() => {
    if (!saveToast) return;
    const t = window.setTimeout(() => setSaveToast(null), 3500);
    return () => window.clearTimeout(t);
  }, [saveToast]);

  const save = async () => {
    const fullName = [form.first, form.last].filter(Boolean).join(" ").trim();
    try {
      await update.mutateAsync({
        full_name: fullName || null,
        timezone: form.timezone || null,
        phone_cc: form.phoneCc || null,
        phone_number: form.phone || null,
        telegram: form.telegram || null,
      });
      setSaveToast({ msg: "Профиль сохранён", variant: "success" });
    } catch (e) {
      const msg =
        (e as { response?: { data?: { error?: { message?: string } } } })
          ?.response?.data?.error?.message ?? "Server error";
      setSaveToast({
        msg: `Не удалось сохранить: ${msg}`,
        variant: "error",
      });
    }
  };

  if (!user) return null;

  return (
    <>
      <StSectionHead
        title="Profile"
        desc="Your personal account. Organization-wide settings live in the Organization tab."
      />

      <div
        className="card card-pad stack stack-16"
        style={{ maxWidth: 680, marginBottom: 16 }}
      >
        <div style={{ fontSize: 13.5, fontWeight: 600 }}>Personal info</div>
        <div
          style={{
            display: "grid",
            gridTemplateColumns: "1fr 1fr",
            gap: 12,
          }}
        >
          <div className="field">
            <label className="label">First name</label>
            <input
              className="input"
              value={form.first}
              onChange={(e) => set("first", e.target.value)}
            />
          </div>
          <div className="field">
            <label className="label">Last name</label>
            <input
              className="input"
              value={form.last}
              onChange={(e) => set("last", e.target.value)}
            />
          </div>
        </div>
        <div className="field">
          <label className="label">
            Email{" "}
            <span className="muted" style={{ fontWeight: 400 }}>
              (login)
            </span>
          </label>
          <div style={{ display: "flex", gap: 8 }}>
            <input
              className="input"
              readOnly
              value={form.email}
              style={{ flex: 1, background: "var(--c-surface-sunk)" }}
            />
            <Btn
              icon={I.edit}
              onClick={() => {
                setEmailFlow(0);
                setNewEmail("");
                setCode("");
              }}
            >
              Change
            </Btn>
          </div>
        </div>
      </div>

      {/* Personal delivery channels for ASO notifications. Lives here until
       *  the rank-tracker modal ships; the same card is meant to be dropped
       *  into that modal. */}
      <div style={{ maxWidth: 680, marginBottom: 16 }}>
        <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 8 }}>
          Notification channels
        </div>
        <NotificationChannelsCard />
      </div>

      <div className="card" style={{ maxWidth: 680, marginBottom: 16 }}>
        <div className="card-head">
          <h3>Organizations</h3>
        </div>
        {/* `.dt` carries a global `min-width: 1600px` for the wide
         *  Ads-Manager grids; inside this 680-px card the table
         *  would otherwise overflow horizontally and shove the
         *  Switch button off to the right. Override locally so the
         *  table fills the card. */}
        <table className="dt" style={{ minWidth: 0 }}>
          <thead>
            <tr>
              <th>Organization</th>
              <th>Role</th>
              <th style={{ width: 120, textAlign: "right" }}></th>
            </tr>
          </thead>
          <tbody>
            {user.orgs.map((o) => {
              const active = user.org_id === o.org_id;
              return (
                <tr
                  key={o.org_id}
                  style={{
                    cursor: active ? "default" : "pointer",
                    background: active ? "var(--c-accent-50)" : "transparent",
                  }}
                  onClick={() => {
                    if (!active) void switchOrg(o.org_public_id);
                  }}
                >
                  <td>
                    <b>{o.org_name}</b>{" "}
                    {active && (
                      <span
                        className="badge accent"
                        style={{ marginLeft: 8, fontSize: 10.5 }}
                      >
                        Current
                      </span>
                    )}
                  </td>
                  <td>
                    <span className="badge">{o.role}</span>
                  </td>
                  <td style={{ textAlign: "right" }}>
                    {!active && <Btn>Switch</Btn>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Plain card (no .stack-16) — heading + desc need a tight
          gap, and `.stack-16` plus a negative marginTop hack on the
          desc made it visually overlap the heading ("поехала
          вёрстка"). Explicit margins are clearer. */}
      <div
        className="card card-pad"
        style={{ maxWidth: 680, marginBottom: 16 }}
      >
        <div style={{ fontSize: 13.5, fontWeight: 600, marginBottom: 4 }}>
          Timezone
        </div>
        <div
          className="muted"
          style={{ fontSize: 12.5, lineHeight: 1.5, marginBottom: 14 }}
        >
          Used in all schedules — notifications, reports, autopilot runs.
        </div>
        <div className="field">
          <select
            className="select"
            value={form.timezone}
            onChange={(e) => set("timezone", e.target.value)}
          >
            <option value="">— pick a timezone —</option>
            {TZ_OPTIONS.map((tz) => (
              <option key={tz.value} value={tz.value}>
                {tz.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <div
        className="card card-pad stack stack-16"
        style={{ maxWidth: 680, marginBottom: 16 }}
      >
        <div style={{ fontSize: 13.5, fontWeight: 600 }}>
          Contact methods{" "}
          <span className="muted" style={{ fontWeight: 400, fontSize: 12 }}>
            · optional
          </span>
        </div>
        <div className="field">
          <label className="label">Phone</label>
          <div style={{ display: "flex", gap: 8 }}>
            <select
              className="select"
              value={form.phoneCc}
              onChange={(e) => set("phoneCc", e.target.value)}
              style={{ width: 90 }}
            >
              {COUNTRY_CODES.map((c) => (
                <option key={c} value={c}>
                  {c}
                </option>
              ))}
            </select>
            <input
              className="input"
              placeholder="555 123 456"
              value={form.phone}
              onChange={(e) => set("phone", e.target.value)}
              style={{ flex: 1 }}
            />
          </div>
        </div>
        <div className="field">
          <label className="label">Telegram</label>
          <input
            className="input mono"
            placeholder="@username"
            value={form.telegram}
            onChange={(e) => set("telegram", e.target.value)}
          />
        </div>
      </div>

      <div
        style={{
          display: "flex",
          justifyContent: "flex-end",
          gap: 8,
          maxWidth: 680,
          marginBottom: 18,
        }}
      >
        <Btn disabled={!dirty} onClick={() => setForm(initial)}>
          Cancel
        </Btn>
        <Btn
          variant="primary"
          disabled={!dirty || update.isPending}
          onClick={() => void save()}
        >
          Save changes
        </Btn>
      </div>

      {/* Sign-out + change-password — secondary actions kept on
          the Profile tab (the design's prototype puts logout in the
          sidebar avatar but we keep it reachable here too for users
          who'd otherwise hunt for it). Visually separated from the
          dirty-check Save row above. */}
      <div
        className="card card-pad"
        style={{
          maxWidth: 680,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
          marginBottom: 16,
        }}
      >
        <div>
          <div style={{ fontWeight: 500, fontSize: 14 }}>Change password</div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            Rotate your password — you&apos;ll be signed out of other devices.
          </div>
        </div>
        <a className="btn" href="/change-password">
          Change password
        </a>
      </div>
      <div
        className="card card-pad"
        style={{
          maxWidth: 680,
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          gap: 16,
        }}
      >
        <div>
          <div style={{ fontWeight: 500, fontSize: 14 }}>Sign out</div>
          <div className="muted" style={{ fontSize: 12.5, marginTop: 2 }}>
            End your session on this device.
          </div>
        </div>
        <Btn variant="danger" onClick={() => void logout()}>
          Log out
        </Btn>
      </div>

      {emailFlow != null && (
        <div
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(15,18,30,.45)",
            zIndex: 200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
          onClick={() => setEmailFlow(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              width: 380,
              background: "var(--c-surface)",
              borderRadius: 14,
              padding: 22,
              boxShadow: "var(--sh-3)",
            }}
          >
            {emailFlow === 0 && (
              <>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
                  Change login email
                </h3>
                <div
                  className="muted"
                  style={{ fontSize: 12.5, marginTop: 8, marginBottom: 14 }}
                >
                  We&apos;ll send a 6-digit code to the new address. Login
                  email changes only after you enter the code. Your password
                  is required because the code goes to the new address — so
                  without it, an open session would be enough to move the
                  account somewhere you cannot follow. We also let the current
                  address know.
                </div>
                <input
                  className="input"
                  placeholder="new@email.com"
                  value={newEmail}
                  onChange={(e) => setNewEmail(e.target.value)}
                />
                <input
                  className="input"
                  type="password"
                  autoComplete="current-password"
                  placeholder="Your current password"
                  value={emailPassword}
                  onChange={(e) => setEmailPassword(e.target.value)}
                  style={{ marginTop: 10 }}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    marginTop: 18,
                  }}
                >
                  <Btn onClick={() => setEmailFlow(null)}>Cancel</Btn>
                  <Btn
                    variant="primary"
                    disabled={
                      !newEmail.includes("@") ||
                      !emailPassword ||
                      initEmail.isPending
                    }
                    onClick={async () => {
                      await initEmail.mutateAsync({
                        new_email: newEmail,
                        current_password: emailPassword,
                      });
                      setEmailPassword("");
                      setEmailFlow(1);
                    }}
                  >
                    Send code
                  </Btn>
                </div>
              </>
            )}
            {emailFlow === 1 && (
              <>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
                  Enter the code
                </h3>
                <div
                  className="muted"
                  style={{ fontSize: 12.5, marginTop: 8, marginBottom: 14 }}
                >
                  Sent to <b>{newEmail}</b>. The code expires in 10 minutes.
                </div>
                <input
                  className="input mono"
                  placeholder="123 456"
                  value={code}
                  onChange={(e) => setCode(e.target.value)}
                />
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 8,
                    marginTop: 18,
                  }}
                >
                  <Btn onClick={() => setEmailFlow(0)}>Back</Btn>
                  <Btn
                    variant="primary"
                    disabled={
                      code.replace(/\s/g, "").length < 6 ||
                      confirmEmail.isPending
                    }
                    onClick={async () => {
                      await confirmEmail.mutateAsync(code);
                      setEmailFlow(2);
                    }}
                  >
                    Confirm
                  </Btn>
                </div>
              </>
            )}
            {emailFlow === 2 && (
              <>
                <h3 style={{ margin: 0, fontSize: 15, fontWeight: 600 }}>
                  Email updated
                </h3>
                <div
                  className="muted"
                  style={{ fontSize: 12.5, marginTop: 8, marginBottom: 14 }}
                >
                  Your login email has been updated. Other sessions on this
                  account have been signed out.
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "flex-end",
                    marginTop: 18,
                  }}
                >
                  <Btn variant="primary" onClick={() => setEmailFlow(null)}>
                    Done
                  </Btn>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {saveToast && (
        <StatusToast message={saveToast.msg} variant={saveToast.variant} />
      )}
    </>
  );
}
